import { Component, OnInit, inject } from '@angular/core';
import {
  AuthenticationMethodType,
  BankIdAuthenticationTicket,
  HintCode,
  PollResponse,
  StatusCode
} from '../../../backend/model';

import { AuthenticationContextDatasourceService, BankIdPollingService } from '../../../backend/datasource';
import { BankIdService } from '../../../backend/api';
import { BankIdAutoStartUtil, UserAgentUtil } from '../../util';
import { timer } from 'rxjs';
import { Theme } from '../../../backend/model/theme.model';
import { ThemeHandlerService } from '../../service/theme-handler.service';
import { LoadingSpinnerComponent } from '../../../shared/component/loading-spinner/loading-spinner.component';
import { IDCardModule } from '@inera/ids-angular/components/card';
import { IDButtonModule } from '@inera/ids-angular/components/button';
import { IDLinkModule } from '@inera/ids-angular/components/link';
import { NgTemplateOutlet } from '@angular/common';
import { ThemeType } from '../../../backend/model/theme-type';

@Component({
  selector: 'bank-id-authentication',
  imports: [LoadingSpinnerComponent, IDCardModule, IDButtonModule, IDLinkModule, NgTemplateOutlet],
  templateUrl: './bank-id-authentication.component.html',
  styleUrls: ['./bank-id-authentication.component.scss']
})
export class BankIDAuthenticationComponent implements OnInit {
  private authenticationContextDatasource = inject(AuthenticationContextDatasourceService);
  private bankIdPollingService = inject(BankIdPollingService);
  private bankIdService = inject(BankIdService);
  private themeHandler = inject(ThemeHandlerService);

  public base64Prefix = 'data:image/jpeg;base64,';
  public ticket: BankIdAuthenticationTicket;
  public pending = false;
  public qrToken = null;
  public qrTokenB64 = null;
  public displayQrCode = true;
  public theme: Theme;
  public themeType = ThemeType;

  public isSameDevice = false;
  public isAndroid = false;
  public isStarting = false;

  public clientInfo;

  ngOnInit(): void {
    this.themeHandler.getTheme().subscribe(theme => {
      this.theme = theme.value.value;
    });

    this.isSameDevice =
      this.authenticationContextDatasource.getAuthMethodType() === AuthenticationMethodType.BANK_ID_SAME_DEVICE;

    this.clientInfo = this.authenticationContextDatasource.getClientInfo();
    this.isAndroid = UserAgentUtil.isAndroid();
    this.getTicket();
  }

  public getMethodTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'underskriften' : 'inloggningen';
  }

  public cancelAuthentication(): void {
    this.bankIdService.cancelAuthentication(
      this.authenticationContextDatasource.getSessionId(),
      this.authenticationContextDatasource.getIdp()
    );
  }

  public getTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'Jag signerar hos' : 'Jag legitimerar mig hos';
  }

  public manualAutostart(): void {
    this.autoStart(true);
  }

  public isSingleMethod(): boolean {
    return this.authenticationContextDatasource.getIsSingleMethod();
  }

  private getTicket(): void {
    this.bankIdService
      .authenticationRequest(
        this.authenticationContextDatasource.getAuthMethodType(),
        this.authenticationContextDatasource.getSessionId(),
        this.authenticationContextDatasource.getIdp()
      )
      .subscribe(ticket => {
        this.ticket = ticket;
        this.bankIdService.setTicket(this.ticket);

        this.qrTokenB64 = (ticket as any).qrStartTokenCodeB64;

        this.startPoll();

        if (!this.isAndroid || (this.isAndroid && !this.authenticationContextDatasource.getIsSingleMethod())) {
          this.autoStart(false);
        }
      });
  }

  private autoStart(manualAutoStart: boolean): void {
    if (this.isSameDevice || manualAutoStart) {
      this.tryStart();
    }
  }

  private tryStart(): void {
    if (this.isStarting === true) {
      return;
    }

    this.isStarting = true;

    timer(3000).subscribe(() => {
      this.isStarting = false;
    });

    BankIdAutoStartUtil.autoStart(this.ticket);
  }

  private startPoll(): void {
    this.bankIdPollingService
      .connect(
        this.authenticationContextDatasource.getSessionId(),
        this.isSameDevice,
        this.authenticationContextDatasource.getIdp()
      )
      .subscribe(response => {
        if (this.evaluateIfStartedOrCompleted(response)) {
          if (response?.redirectUrl?.length) {
            window.location.href = response.redirectUrl;
          } else {
            this.pending = true;
          }
        } else {
          if (response?.qrStartTokenCodeB64?.length) {
            this.qrTokenB64 = response.qrStartTokenCodeB64;
          }
        }
      });
  }

  private evaluateIfStartedOrCompleted(data: PollResponse): boolean {
    return (
      data !== null &&
      ((data.statusCode === StatusCode.PENDING && data.hintCode === HintCode.PENDING_STARTED) ||
        data.statusCode === StatusCode.COMPLETE)
    );
  }
}
